import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import {
  Calendar,
  Download,
  RefreshCw,
  Filter,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Package,
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  BarChart3,
  Globe,
  Users,
  Building2,
  Briefcase,
  FileText,
  Loader2
} from 'lucide-react';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import LoadingSpinner from '../../../shared/components/LoadingSpinner';

// Chart colors
const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#14b8a6'];

// BigQuery Cost Service
class BigQueryCostServiceClass {
  constructor() {
    this.functions = null;
    this.auth = null;
  }

  initialize() {
    if (!this.functions) this.functions = getFunctions();
    if (!this.auth) this.auth = getAuth();
  }

  async waitForAuth() {
    this.initialize();
    return new Promise((resolve, reject) => {
      const unsubscribe = this.auth.onAuthStateChanged(user => {
        unsubscribe();
        if (user) resolve(user);
        else reject(new Error('Not authenticated'));
      }, reject);
    });
  }

  async getCostAllocationData(filters = {}, startDate = null, endDate = null) {
    try {
      await this.waitForAuth();
      const getCostData = httpsCallable(this.functions, 'getCostAllocationWithDates', { timeout: 330000 });
      const result = await getCostData({
        startDate: startDate || '2025-07-01',
        endDate: endDate || '2025-11-30',
        filters: filters || {}
      });
      return result.data?.data || result.data || [];
    } catch (error) {
      console.error('BigQuery query error:', error);
      throw error;
    }
  }
}

const BigQueryCostService = new BigQueryCostServiceClass();

/**
 * First Sale Review - Analysis of FS and FS3 terms
 * Redesigned with pivot table, charts, and hierarchical details
 */
export default function FirstSaleReview({ onBackToDashboard, embedded = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [error, setError] = useState(null);

  // Date range filters
  const [startDate, setStartDate] = useState('2025-07-01');
  const [endDate, setEndDate] = useState('2025-11-30');

  // Post-load filters (multi-select arrays)
  const [postFilters, setPostFilters] = useState({
    division: [],
    customer: [],
    office: [],
    country: []
  });

  // Pivot filters
  const [pivotFilters, setPivotFilters] = useState({
    customer: [],
    office: [],
    country: []
  });

  // Selected month from pivot
  const [selectedMonth, setSelectedMonth] = useState(null);

  // Selected LC beneficiary
  const [selectedBeneficiary, setSelectedBeneficiary] = useState(null);

  // LC code → report name lookup
  const [lcLookup, setLcLookup] = useState({});

  // Ignore list from settings
  const [ignoreList, setIgnoreList] = useState(new Set());

  // Outlier exclusion toggle
  const [excludeOutliers, setExcludeOutliers] = useState(false);

  // Expanded states for hierarchical detail table
  const [expandedInvoices, setExpandedInvoices] = useState(new Set());
  const [expandedPOs, setExpandedPOs] = useState(new Set());

  // Section visibility
  const [showCharts, setShowCharts] = useState(true);
  const [showPivot, setShowPivot] = useState(true);
  const [showDetail, setShowDetail] = useState(true);

  // Dropdown states
  const [openDropdown, setOpenDropdown] = useState(null);
  const containerRef = useRef(null);

  // PDF Export state and refs
  const [exportingPdf, setExportingPdf] = useState(false);
  const chartsRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Filter to only FS and FS3 terms, excluding ignored invoices
  const fsData = useMemo(() => {
    return data.filter(item => {
      const terms = item.terms2 || item.Terms || '';
      if (terms !== 'FS' && terms !== 'FS3') return false;
      if (ignoreList.size > 0 && ignoreList.has(item.commercial_invoice)) return false;
      return true;
    });
  }, [data, ignoreList]);

  // Get unique values for dropdowns
  const dropdownOptions = useMemo(() => {
    if (!fsData.length) return { divisions: [], customers: [], offices: [], countries: [] };
    const divisions = [...new Set(fsData.map(r => r.division_name).filter(Boolean))].sort();
    const customers = [...new Set(fsData.map(r => r.customer).filter(Boolean))].sort();
    const offices = [...new Set(fsData.map(r => r.office).filter(Boolean))].sort();
    const countries = [...new Set(fsData.map(r => r.country_of_origin).filter(Boolean))].sort();
    return { divisions, customers, offices, countries };
  }, [fsData]);

  // Apply post-load filters
  const filteredData = useMemo(() => {
    let result = fsData;
    if (postFilters.division.length > 0) {
      result = result.filter(r => postFilters.division.includes(r.division_name));
    }
    if (postFilters.customer.length > 0) {
      result = result.filter(r => postFilters.customer.includes(r.customer));
    }
    if (postFilters.office.length > 0) {
      result = result.filter(r => postFilters.office.includes(r.office));
    }
    if (postFilters.country.length > 0) {
      result = result.filter(r => postFilters.country.includes(r.country_of_origin));
    }
    return result;
  }, [fsData, postFilters]);

  // Apply pivot filters
  const pivotFilteredData = useMemo(() => {
    let result = filteredData;
    if (pivotFilters.customer.length > 0) {
      result = result.filter(r => pivotFilters.customer.includes(r.customer));
    }
    if (pivotFilters.office.length > 0) {
      result = result.filter(r => pivotFilters.office.includes(r.office));
    }
    if (pivotFilters.country.length > 0) {
      result = result.filter(r => pivotFilters.country.includes(r.country_of_origin));
    }
    return result;
  }, [filteredData, pivotFilters]);

  // Group data by commercial invoice
  const invoiceData = useMemo(() => {
    const grouped = {};

    pivotFilteredData.forEach(item => {
      const invoice = item.commercial_invoice || 'N/A';
      if (!grouped[invoice]) {
        grouped[invoice] = {
          commercial_invoice: invoice,
          termsSet: new Set(),
          divisionsSet: new Set(),
          managersSet: new Set(),
          customersSet: new Set(),
          countriesSet: new Set(),
          factoriesSet: new Set(),
          lcBeneficiariesSet: new Set(),
          productionNumbersSet: new Set(),
          shipmentNumbersSet: new Set(),
          total_fob: 0,
          total_line_entered_value: 0,
          total_system_duty: 0,
          total_actual_duty: 0,
          total_system_tariff: 0,
          total_actual_tariff: 0,
          total_qty: 0,
          last_receipt_month: null,
          office: item.office || 'N/A',
          warehouse: item.warehouse || 'N/A',
          lines: []
        };
      }

      const g = grouped[invoice];
      const terms = item.terms2 || item.Terms || '';
      if (terms) g.termsSet.add(terms);
      if (item.division_name) g.divisionsSet.add(item.division_name);
      if (item.Production_Manager) g.managersSet.add(item.Production_Manager);
      if (item.customer) g.customersSet.add(item.customer);
      if (item.country_of_origin) g.countriesSet.add(item.country_of_origin);
      if (item.factory) g.factoriesSet.add(item.factory.replace(/^"|"$/g, '').trim());
      if (item.lc_beneficiary) g.lcBeneficiariesSet.add(item.lc_beneficiary);

      const prodNum = item.production_number || '';
      if (prodNum) g.productionNumbersSet.add(prodNum);
      const shipNum = item.shipment_num || '';
      if (shipNum) g.shipmentNumbersSet.add(shipNum);

      const qty = parseFloat(item.qty) || 0;
      g.total_fob += parseFloat(item.total_fob) || 0;
      g.total_line_entered_value += parseFloat(item.total_line_entered_value) || 0;
      g.total_system_duty += (parseFloat(item.standard_duty_cost) || 0) * qty;
      g.total_actual_duty += (parseFloat(item.duty_only_per_unit) || parseFloat(item.total_duty_per_unit) || 0) * qty;
      g.total_system_tariff += (parseFloat(item.standard_tariff_cost) || 0) * qty;
      g.total_actual_tariff += parseFloat(item.total_actual_tariff) || (parseFloat(item.total_tariffs) || 0) * qty;
      g.total_qty += qty;

      const itemMonth = item.last_receipt_month || '';
      if (itemMonth && (!g.last_receipt_month || itemMonth > g.last_receipt_month)) {
        g.last_receipt_month = itemMonth;
      }

      g.lines.push(item);
    });

    return Object.values(grouped).map(g => {
      const fobVariance = g.total_fob > 0
        ? ((g.total_line_entered_value - g.total_fob) / g.total_fob) * 100
        : 0;
      const dutyVariance = g.total_system_duty > 0
        ? ((g.total_actual_duty - g.total_system_duty) / g.total_system_duty) * 100
        : 0;
      const tariffVariance = g.total_system_tariff > 0
        ? ((g.total_actual_tariff - g.total_system_tariff) / g.total_system_tariff) * 100
        : 0;

      let receiptMonth = 'N/A';
      if (g.last_receipt_month) {
        const [year, month] = g.last_receipt_month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        receiptMonth = `${monthNames[parseInt(month, 10) - 1]} ${year}`;
      }

      return {
        ...g,
        terms: Array.from(g.termsSet).join(', '),
        division: Array.from(g.divisionsSet).join(', '),
        production_manager: Array.from(g.managersSet).join(', '),
        customer: Array.from(g.customersSet).join(', '),
        country_of_origin: Array.from(g.countriesSet).join(', '),
        factory: Array.from(g.factoriesSet).join(', '),
        lc_beneficiary: Array.from(g.lcBeneficiariesSet).join(', '),
        productionNumbers: Array.from(g.productionNumbersSet),
        shipmentNumbers: Array.from(g.shipmentNumbersSet),
        po_count: g.productionNumbersSet.size,
        fobVariance,
        dutyVariance,
        tariffVariance,
        fob_variance_amount: g.total_line_entered_value - g.total_fob,
        duty_variance_amount: g.total_actual_duty - g.total_system_duty,
        tariff_variance_amount: g.total_actual_tariff - g.total_system_tariff,
        receipt_month: receiptMonth
      };
    });
  }, [pivotFilteredData]);

  // Outlier detection using IQR on per-invoice FOB Var %
  const outlierInfo = useMemo(() => {
    const pcts = invoiceData
      .filter(inv => inv.total_fob > 0)
      .map(inv => inv.fobVariance); // fobVariance is already ((custom_value - fob) / fob) * 100

    if (pcts.length < 4) return { outlierInvoices: new Set(), count: 0, lowerBound: null, upperBound: null };

    const sorted = [...pcts].sort((a, b) => a - b);
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const iqr = q3 - q1;
    const lowerBound = q1 - 1.5 * iqr;
    const upperBound = q3 + 1.5 * iqr;

    const outlierInvoices = new Set();
    invoiceData.forEach(inv => {
      if (inv.total_fob > 0 && (inv.fobVariance < lowerBound || inv.fobVariance > upperBound)) {
        outlierInvoices.add(inv.commercial_invoice || inv.invoice);
      }
    });

    return { outlierInvoices, count: outlierInvoices.size, lowerBound, upperBound };
  }, [invoiceData]);

  // Filtered invoice data (with outlier exclusion applied)
  const cleanedInvoiceData = useMemo(() => {
    if (!excludeOutliers || outlierInfo.count === 0) return invoiceData;
    return invoiceData.filter(inv => {
      const key = inv.commercial_invoice || inv.invoice;
      return !outlierInfo.outlierInvoices.has(key);
    });
  }, [invoiceData, excludeOutliers, outlierInfo]);

  // Apply month and beneficiary filters
  const monthFilteredInvoices = useMemo(() => {
    let result = cleanedInvoiceData;
    if (selectedMonth) {
      result = result.filter(inv => inv.last_receipt_month === selectedMonth);
    }
    if (selectedBeneficiary) {
      result = result.filter(inv => inv.lc_beneficiary && inv.lc_beneficiary.includes(selectedBeneficiary));
    }
    return result;
  }, [cleanedInvoiceData, selectedMonth, selectedBeneficiary]);

  // Pivot data (grouped by month)
  const pivotData = useMemo(() => {
    const grouped = {};

    cleanedInvoiceData.forEach(inv => {
      const month = inv.last_receipt_month || 'Unknown';
      if (!grouped[month]) {
        grouped[month] = {
          month,
          invoice_count: 0,
          total_fob: 0,
          total_custom_value: 0,
          fob_variance_amount: 0,
          total_system_duty: 0,
          total_actual_duty: 0,
          duty_variance_amount: 0,
          total_system_tariff: 0,
          total_actual_tariff: 0,
          tariff_variance_amount: 0
        };
      }
      grouped[month].invoice_count += 1;
      grouped[month].total_fob += inv.total_fob;
      grouped[month].total_custom_value += inv.total_line_entered_value;
      grouped[month].fob_variance_amount += inv.fob_variance_amount;
      grouped[month].total_system_duty += inv.total_system_duty;
      grouped[month].total_actual_duty += inv.total_actual_duty;
      grouped[month].duty_variance_amount += inv.duty_variance_amount;
      grouped[month].total_system_tariff += inv.total_system_tariff;
      grouped[month].total_actual_tariff += inv.total_actual_tariff;
      grouped[month].tariff_variance_amount += inv.tariff_variance_amount;
    });

    return Object.values(grouped).sort((a, b) => a.month.localeCompare(b.month));
  }, [cleanedInvoiceData]);

  // Beneficiary pivot data (grouped by LC beneficiary / factory)
  const beneficiaryPivotData = useMemo(() => {
    const source = selectedMonth ? cleanedInvoiceData.filter(inv => inv.last_receipt_month === selectedMonth) : cleanedInvoiceData;
    const grouped = {};

    source.forEach(inv => {
      // Use lc_beneficiary from production data, fallback to factory
      const beneficiaries = inv.lc_beneficiary ? inv.lc_beneficiary.split(', ') : (inv.factory ? inv.factory.split(', ') : ['Unknown']);
      beneficiaries.forEach(f => {
        const code = f || 'Unknown';
        if (!grouped[code]) {
          grouped[code] = {
            factory: lcLookup[code] || code,
            lc_code: code,
            invoice_count: 0,
            total_fob: 0,
            total_custom_value: 0,
            fob_variance_amount: 0,
            total_system_duty: 0,
            total_actual_duty: 0,
            duty_variance_amount: 0,
            total_system_tariff: 0,
            total_actual_tariff: 0,
            tariff_variance_amount: 0
          };
        }
        grouped[code].invoice_count += 1;
        grouped[code].total_fob += inv.total_fob;
        grouped[code].total_custom_value += inv.total_line_entered_value;
        grouped[code].fob_variance_amount += inv.fob_variance_amount;
        grouped[code].total_system_duty += inv.total_system_duty;
        grouped[code].total_actual_duty += inv.total_actual_duty;
        grouped[code].duty_variance_amount += inv.duty_variance_amount;
        grouped[code].total_system_tariff += inv.total_system_tariff;
        grouped[code].total_actual_tariff += inv.total_actual_tariff;
        grouped[code].tariff_variance_amount += inv.tariff_variance_amount;
      });
    });

    return Object.values(grouped).sort((a, b) => b.total_fob - a.total_fob);
  }, [cleanedInvoiceData, selectedMonth, lcLookup]);

  const beneficiaryGrandTotals = useMemo(() => {
    return beneficiaryPivotData.reduce((acc, row) => ({
      invoice_count: acc.invoice_count + row.invoice_count,
      total_fob: acc.total_fob + row.total_fob,
      total_custom_value: acc.total_custom_value + row.total_custom_value,
      fob_variance_amount: acc.fob_variance_amount + row.fob_variance_amount,
      duty_variance_amount: acc.duty_variance_amount + row.duty_variance_amount,
      tariff_variance_amount: acc.tariff_variance_amount + row.tariff_variance_amount
    }), { invoice_count: 0, total_fob: 0, total_custom_value: 0, fob_variance_amount: 0, duty_variance_amount: 0, tariff_variance_amount: 0 });
  }, [beneficiaryPivotData]);

  // Grand totals
  const grandTotals = useMemo(() => {
    return pivotData.reduce((acc, row) => ({
      invoice_count: acc.invoice_count + row.invoice_count,
      total_fob: acc.total_fob + row.total_fob,
      total_custom_value: acc.total_custom_value + row.total_custom_value,
      fob_variance_amount: acc.fob_variance_amount + row.fob_variance_amount,
      total_system_duty: acc.total_system_duty + row.total_system_duty,
      total_actual_duty: acc.total_actual_duty + row.total_actual_duty,
      duty_variance_amount: acc.duty_variance_amount + row.duty_variance_amount,
      total_system_tariff: acc.total_system_tariff + row.total_system_tariff,
      total_actual_tariff: acc.total_actual_tariff + row.total_actual_tariff,
      tariff_variance_amount: acc.tariff_variance_amount + row.tariff_variance_amount
    }), {
      invoice_count: 0,
      total_fob: 0,
      total_custom_value: 0,
      fob_variance_amount: 0,
      total_system_duty: 0,
      total_actual_duty: 0,
      duty_variance_amount: 0,
      total_system_tariff: 0,
      total_actual_tariff: 0,
      tariff_variance_amount: 0
    });
  }, [pivotData]);

  // Summary stats
  const stats = useMemo(() => {
    const fobVariancePct = grandTotals.total_fob > 0
      ? (grandTotals.fob_variance_amount / grandTotals.total_fob) * 100
      : 0;
    const dutyVariancePct = grandTotals.total_system_duty > 0
      ? (grandTotals.duty_variance_amount / grandTotals.total_system_duty) * 100
      : 0;
    const tariffVariancePct = grandTotals.total_system_tariff > 0
      ? (grandTotals.tariff_variance_amount / grandTotals.total_system_tariff) * 100
      : 0;

    return {
      totalInvoices: grandTotals.invoice_count,
      totalFob: grandTotals.total_fob,
      fobVarianceAmount: grandTotals.fob_variance_amount,
      fobVariancePct,
      dutyVarianceAmount: grandTotals.duty_variance_amount,
      dutyVariancePct,
      tariffVarianceAmount: grandTotals.tariff_variance_amount,
      tariffVariancePct
    };
  }, [grandTotals]);

  // Country distribution chart data
  const countryData = useMemo(() => {
    const grouped = {};
    monthFilteredInvoices.forEach(inv => {
      const country = inv.country_of_origin || 'Unknown';
      if (!grouped[country]) grouped[country] = 0;
      grouped[country] += inv.total_fob;
    });
    return Object.entries(grouped)
      .map(([name, value], idx) => ({ name, value, color: COLORS[idx % COLORS.length] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [monthFilteredInvoices]);

  // Office distribution chart data (FOB by Office)
  const officeData = useMemo(() => {
    const grouped = {};
    monthFilteredInvoices.forEach(inv => {
      const office = inv.office || 'Unknown';
      if (!grouped[office]) grouped[office] = 0;
      grouped[office] += inv.total_fob;
    });
    return Object.entries(grouped)
      .map(([name, value], idx) => ({ name, value, color: COLORS[idx % COLORS.length] }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [monthFilteredInvoices]);

  // Customer FOB Variance impact data
  const customerImpactData = useMemo(() => {
    const grouped = {};
    monthFilteredInvoices.forEach(inv => {
      const customer = inv.customer || 'Unknown';
      if (!grouped[customer]) grouped[customer] = 0;
      grouped[customer] += inv.fob_variance_amount;
    });
    return Object.entries(grouped)
      .map(([name, value]) => ({
        name: name.length > 15 ? name.substring(0, 15) + '...' : name,
        value: Math.round(value)
      }))
      .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
      .slice(0, 10);
  }, [monthFilteredInvoices]);

  // Hierarchical detail data
  const hierarchicalData = useMemo(() => {
    return monthFilteredInvoices.map(inv => {
      const poMap = new Map();

      inv.lines.forEach(line => {
        const po = line.production_number || 'Unknown';
        if (!poMap.has(po)) {
          poMap.set(po, {
            po,
            shipmentNumbersSet: new Set(),
            styles: [],
            totals: { qty: 0, fob: 0, custom_value: 0, system_duty: 0, actual_duty: 0 }
          });
        }

        const poData = poMap.get(po);
        const shipNum = line.shipment_num || '';
        if (shipNum) poData.shipmentNumbersSet.add(shipNum);
        const qty = parseFloat(line.qty) || 0;
        const fob = parseFloat(line.total_fob) || 0;
        const customValue = parseFloat(line.total_line_entered_value) || 0;

        poData.styles.push({
          style: line.style,
          color: line.color,
          qty,
          fob,
          custom_value: customValue,
          variance: fob > 0 ? ((customValue - fob) / fob) * 100 : 0
        });

        poData.totals.qty += qty;
        poData.totals.fob += fob;
        poData.totals.custom_value += customValue;
      });

      return {
        ...inv,
        pos: Array.from(poMap.values()).map(p => ({
          ...p,
          shipmentNumbers: Array.from(p.shipmentNumbersSet)
        }))
      };
    });
  }, [monthFilteredInvoices]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedMonth(null);
      setExpandedInvoices(new Set());
      setExpandedPOs(new Set());

      const allData = await BigQueryCostService.getCostAllocationData({}, startDate, endDate);
      setData(allData || []);
      console.log(`✅ Loaded ${(allData || []).length} records`);
    } catch (err) {
      console.error('❌ Error loading data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
    // CHA-404 — resolve beneficiary codes to company names from the ERP location master. This
    // replaces the 22-row `lc_beneficiary_codes` collection, which covered a fraction of the codes
    // in use and carried placeholder rows whose "name" was just the code repeated. `lookup_locations`
    // is the full Blue Cherry master (650 codes) refreshed from the BCADMIN export.
    getDocs(collection(getFirestore(), 'lookup_locations')).then(snap => {
      const map = {};
      snap.docs.forEach(d => {
        const data = d.data();
        // A row whose name merely repeats its code resolves to nothing — leaving it out lets the
        // caller's `lcLookup[code] || code` fallback render the bare code, which is the same
        // output without pretending the master knows the company.
        const code = String(data.code || '').trim().toUpperCase();
        const name = String(data.name || '').trim();
        // FIRST doc wins, matching composeBeneficiaryFactories and mapLocationDocs. The Lookup
        // Manager mints auto-id docs, so a hand-added code can coexist with the seeded one — and a
        // last-wins map here would resolve the same code to a different company than the
        // Beneficiaries tab and the Power BI mirror do (CodeRabbit).
        if (code && name && name.toUpperCase() !== code && !(code in map)) map[code] = name;
      });
      setLcLookup(map);
    }).catch(err => console.error('Failed to load location master:', err));
    // Load ignore list from BigQuery
    const functions = getFunctions();
    httpsCallable(functions, 'getLookupTableData')({ tableName: 'first_sale_error_ignore_list' }).then(result => {
      const list = result.data?.data || [];
      setIgnoreList(new Set(list.map(r => r.commercial_invoice).filter(Boolean)));
    }).catch(err => console.error('Failed to load ignore list:', err));
  }, []);

  const toggleInvoice = (invoice) => {
    setExpandedInvoices(prev => {
      const next = new Set(prev);
      if (next.has(invoice)) next.delete(invoice);
      else next.add(invoice);
      return next;
    });
  };

  const togglePO = (key) => {
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleMultiSelect = (filterType, filterGroup, value) => {
    const setter = filterGroup === 'post' ? setPostFilters : setPivotFilters;
    setter(prev => {
      const current = prev[filterType] || [];
      if (current.includes(value)) {
        return { ...prev, [filterType]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [filterType]: [...current, value] };
      }
    });
  };

  const clearFilter = (filterType, filterGroup) => {
    const setter = filterGroup === 'post' ? setPostFilters : setPivotFilters;
    setter(prev => ({ ...prev, [filterType]: [] }));
  };

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Summary sheet
    const summaryData = pivotData.map(row => ({
      'Month': row.month,
      'Invoices': row.invoice_count,
      'Total FOB': row.total_fob,
      'Custom Value': row.total_custom_value,
      'FOB Variance $': row.fob_variance_amount,
      'FOB Variance %': row.total_fob > 0 ? ((row.fob_variance_amount / row.total_fob) * 100).toFixed(2) + '%' : 'N/A',
      'Duty Variance $': row.duty_variance_amount,
      'Tariff Variance $': row.tariff_variance_amount
    }));
    summaryData.push({
      'Month': 'Grand Total',
      'Invoices': grandTotals.invoice_count,
      'Total FOB': grandTotals.total_fob,
      'Custom Value': grandTotals.total_custom_value,
      'FOB Variance $': grandTotals.fob_variance_amount,
      'FOB Variance %': grandTotals.total_fob > 0 ? ((grandTotals.fob_variance_amount / grandTotals.total_fob) * 100).toFixed(2) + '%' : 'N/A',
      'Duty Variance $': grandTotals.duty_variance_amount,
      'Tariff Variance $': grandTotals.tariff_variance_amount
    });
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryData), 'Summary');

    // Detail sheet
    const detailData = monthFilteredInvoices.map(inv => ({
      'Commercial Invoice': inv.commercial_invoice,
      'Receipt Month': inv.receipt_month,
      'Terms': inv.terms,
      'Division': inv.division,
      'Customer': inv.customer,
      'PO Manager': inv.production_manager,
      'Country': inv.country_of_origin,
      'Shipment Numbers': inv.shipmentNumbers?.join(', ') || '',
      'PO Count': inv.po_count,
      'System FOB': inv.total_fob,
      'Custom Value': inv.total_line_entered_value,
      'FOB Variance $': inv.fob_variance_amount,
      'FOB Variance %': inv.fobVariance.toFixed(2) + '%',
      'System Duty': inv.total_system_duty,
      'Actual Duty': inv.total_actual_duty,
      'Duty Variance $': inv.duty_variance_amount,
      'Duty Variance %': inv.dutyVariance.toFixed(2) + '%'
    }));
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(detailData), 'Invoices');

    XLSX.writeFile(wb, `First_Sale_Review_${startDate}_to_${endDate}.xlsx`);
  };

  // Professional PDF Export Function
  const exportToPdf = async () => {
    if (exportingPdf) return;

    setExportingPdf(true);

    try {
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      let yPosition = margin;

      // Get selected divisions for header
      const selectedDivisions = postFilters.division.length > 0
        ? postFilters.division.join(', ')
        : 'All Divisions';

      // Helper function to add header with division info
      const addHeader = () => {
        pdf.setFillColor(16, 185, 129); // Green for First Sale
        pdf.rect(0, 0, pageWidth, 18, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.text('First Sale Review Report (FS/FS3)', margin, 11);

        // Division info in header
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        const divisionText = `Division: ${selectedDivisions}`;
        pdf.text(divisionText, pageWidth / 2 - pdf.getTextWidth(divisionText) / 2, 11);

        // Date range on right
        pdf.text(`${startDate} to ${endDate}`, pageWidth - margin - 45, 11);
        pdf.setTextColor(0, 0, 0);
        return 22;
      };

      // Helper function to add footer
      let currentPage = 0;
      const totalPages = beneficiaryPivotData.length > 0 ? 2 : 1;
      const addFooter = () => {
        currentPage++;
        pdf.setFontSize(7);
        pdf.setTextColor(100, 116, 139);
        pdf.text(`Generated: ${new Date().toLocaleString()}`, margin, pageHeight - 4);
        pdf.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin - 15, pageHeight - 4);
        pdf.setTextColor(0, 0, 0);
      };

      // Single Page Layout
      yPosition = addHeader();

      // Outlier exclusion note
      if (excludeOutliers && outlierInfo.count > 0) {
        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'italic');
        pdf.setTextColor(180, 120, 0);
        pdf.text(`* ${outlierInfo.count} outlier invoice${outlierInfo.count !== 1 ? 's' : ''} excluded (FOB Var % outside ${outlierInfo.lowerBound?.toFixed(1)}% to ${outlierInfo.upperBound?.toFixed(1)}%)`, margin, yPosition);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(0, 0, 0);
        yPosition += 4;
      }

      // Summary Stats Row (compact)
      const statsBoxWidth = (pageWidth - margin * 2 - 9) / 4;
      const statsBoxHeight = 18;

      // Stats data - positive = green, negative = red
      const summaryStats = [
        { label: 'Total Invoices', value: stats.totalInvoices.toString(), color: [59, 130, 246] },
        { label: 'Total FOB', value: formatCurrency(stats.totalFob), color: [59, 130, 246] },
        { label: 'FOB Variance', value: formatCurrency(stats.fobVarianceAmount), color: stats.fobVarianceAmount >= 0 ? [16, 185, 129] : [239, 68, 68] },
        { label: 'Duty Variance', value: formatCurrency(stats.dutyVarianceAmount), color: stats.dutyVarianceAmount >= 0 ? [16, 185, 129] : [239, 68, 68] }
      ];

      // Compact stats boxes
      summaryStats.forEach((stat, index) => {
        const x = margin + (statsBoxWidth + 3) * index;
        pdf.setFillColor(248, 250, 252);
        pdf.roundedRect(x, yPosition, statsBoxWidth, statsBoxHeight, 2, 2, 'F');
        pdf.setDrawColor(226, 232, 240);
        pdf.roundedRect(x, yPosition, statsBoxWidth, statsBoxHeight, 2, 2, 'S');

        pdf.setFontSize(7);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(100, 116, 139);
        pdf.text(stat.label, x + 3, yPosition + 6);

        pdf.setFontSize(11);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...stat.color);
        pdf.text(stat.value, x + 3, yPosition + 14);
      });

      yPosition += statsBoxHeight + 4;

      // ===== PIVOT TABLE (Full Width) =====
      const tableWidth = pageWidth - margin * 2;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text('Monthly Summary - First Sale Transactions', margin, yPosition);
      yPosition += 5;

      // Table headers
      const tableHeaders = ['Month', 'Invoices', 'Total FOB', 'Custom Value', 'FOB Var $', 'FOB Var %', 'Duty Var $', 'Tariff Var $', 'Total Var $'];
      const colWidths = [24, 18, 30, 30, 28, 22, 28, 28, 30];

      // Header row
      pdf.setFillColor(241, 245, 249);
      pdf.rect(margin, yPosition, tableWidth, 6, 'F');
      pdf.setFontSize(6);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(71, 85, 105);

      let xPos = margin + 2;
      tableHeaders.forEach((header, i) => {
        pdf.text(header, xPos, yPosition + 4);
        xPos += colWidths[i];
      });
      yPosition += 8;

      // Table data rows (compact - 5mm per row)
      pdf.setFontSize(6);
      pdf.setFont('helvetica', 'normal');
      pivotData.forEach((row, rowIndex) => {
        const fobVarPct = row.total_fob > 0 ? ((row.fob_variance_amount / row.total_fob) * 100) : 0;
        const totalVariance = row.duty_variance_amount + row.tariff_variance_amount;

        // Alternate row colors - draw background first, then text
        if (rowIndex % 2 === 1) {
          pdf.setFillColor(249, 250, 251);
          pdf.rect(margin, yPosition, tableWidth, 5, 'F');
        }

        xPos = margin + 2;
        pdf.setTextColor(30, 41, 59);
        pdf.text(row.month, xPos, yPosition + 3.5);
        xPos += colWidths[0];

        pdf.text(row.invoice_count.toString(), xPos, yPosition + 3.5);
        xPos += colWidths[1];

        pdf.text(formatCurrency(row.total_fob), xPos, yPosition + 3.5);
        xPos += colWidths[2];

        pdf.text(formatCurrency(row.total_custom_value), xPos, yPosition + 3.5);
        xPos += colWidths[3];

        // FOB Variance $ - positive = green, negative = red
        pdf.setTextColor(row.fob_variance_amount >= 0 ? 16 : 239, row.fob_variance_amount >= 0 ? 185 : 68, row.fob_variance_amount >= 0 ? 129 : 68);
        pdf.text(formatCurrency(row.fob_variance_amount), xPos, yPosition + 3.5);
        xPos += colWidths[4];

        // FOB Variance %
        pdf.text(fobVarPct.toFixed(1) + '%', xPos, yPosition + 3.5);
        xPos += colWidths[5];

        // Duty Variance
        pdf.setTextColor(row.duty_variance_amount >= 0 ? 16 : 239, row.duty_variance_amount >= 0 ? 185 : 68, row.duty_variance_amount >= 0 ? 129 : 68);
        pdf.text(formatCurrency(row.duty_variance_amount), xPos, yPosition + 3.5);
        xPos += colWidths[6];

        // Tariff Variance
        pdf.setTextColor(row.tariff_variance_amount >= 0 ? 16 : 239, row.tariff_variance_amount >= 0 ? 185 : 68, row.tariff_variance_amount >= 0 ? 129 : 68);
        pdf.text(formatCurrency(row.tariff_variance_amount), xPos, yPosition + 3.5);
        xPos += colWidths[7];

        // Total Variance (Duty + Tariff)
        pdf.setTextColor(totalVariance >= 0 ? 16 : 239, totalVariance >= 0 ? 185 : 68, totalVariance >= 0 ? 129 : 68);
        pdf.setFont('helvetica', 'bold');
        pdf.text(formatCurrency(totalVariance), xPos, yPosition + 3.5);
        pdf.setFont('helvetica', 'normal');

        yPosition += 5;
      });

      // Grand Total row
      const grandFobVarPct = grandTotals.total_fob > 0 ? ((grandTotals.fob_variance_amount / grandTotals.total_fob) * 100) : 0;
      const grandTotalVariance = grandTotals.duty_variance_amount + grandTotals.tariff_variance_amount;
      pdf.setFillColor(226, 232, 240);
      pdf.rect(margin, yPosition, tableWidth, 5.5, 'F');
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(6);
      pdf.setTextColor(30, 41, 59);

      xPos = margin + 2;
      pdf.text('Grand Total', xPos, yPosition + 4);
      xPos += colWidths[0];
      pdf.text(grandTotals.invoice_count.toString(), xPos, yPosition + 4);
      xPos += colWidths[1];
      pdf.text(formatCurrency(grandTotals.total_fob), xPos, yPosition + 4);
      xPos += colWidths[2];
      pdf.text(formatCurrency(grandTotals.total_custom_value), xPos, yPosition + 4);
      xPos += colWidths[3];

      pdf.setTextColor(grandTotals.fob_variance_amount >= 0 ? 16 : 239, grandTotals.fob_variance_amount >= 0 ? 185 : 68, grandTotals.fob_variance_amount >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotals.fob_variance_amount), xPos, yPosition + 4);
      xPos += colWidths[4];
      pdf.text(grandFobVarPct.toFixed(1) + '%', xPos, yPosition + 4);
      xPos += colWidths[5];

      pdf.setTextColor(grandTotals.duty_variance_amount >= 0 ? 16 : 239, grandTotals.duty_variance_amount >= 0 ? 185 : 68, grandTotals.duty_variance_amount >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotals.duty_variance_amount), xPos, yPosition + 4);
      xPos += colWidths[6];

      pdf.setTextColor(grandTotals.tariff_variance_amount >= 0 ? 16 : 239, grandTotals.tariff_variance_amount >= 0 ? 185 : 68, grandTotals.tariff_variance_amount >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotals.tariff_variance_amount), xPos, yPosition + 4);
      xPos += colWidths[7];

      // Total Variance (Duty + Tariff)
      pdf.setTextColor(grandTotalVariance >= 0 ? 16 : 239, grandTotalVariance >= 0 ? 185 : 68, grandTotalVariance >= 0 ? 129 : 68);
      pdf.text(formatCurrency(grandTotalVariance), xPos, yPosition + 4);

      yPosition += 8;

      // ===== CHARTS (Below Pivot Table) =====
      if (chartsRef.current) {
        try {
          const chartsCanvas = await html2canvas(chartsRef.current, {
            scale: 2,
            backgroundColor: '#ffffff',
            logging: false,
            useCORS: true
          });

          const chartsImg = chartsCanvas.toDataURL('image/png');

          // Calculate chart dimensions to fit remaining space
          const availableChartHeight = pageHeight - yPosition - 10;
          const availableChartWidth = pageWidth - margin * 2;

          // Scale to fit available space
          const imgAspectRatio = chartsCanvas.width / chartsCanvas.height;
          let finalWidth = availableChartWidth;
          let finalHeight = finalWidth / imgAspectRatio;

          // If too tall, scale by height instead
          if (finalHeight > availableChartHeight) {
            finalHeight = availableChartHeight;
            finalWidth = finalHeight * imgAspectRatio;
          }

          // Center the charts horizontally
          const chartX = margin + (availableChartWidth - finalWidth) / 2;

          pdf.addImage(chartsImg, 'PNG', chartX, yPosition, finalWidth, finalHeight);
        } catch (chartError) {
          console.warn('Could not capture charts:', chartError);
          pdf.setFontSize(8);
          pdf.setTextColor(150, 150, 150);
          pdf.text('Charts could not be captured.', margin, yPosition + 10);
        }
      }

      // Add footer to page 1
      addFooter();

      // ===== PAGE 2: LC Beneficiary Table =====
      if (beneficiaryPivotData.length > 0) {
        pdf.addPage();
        yPosition = addHeader();

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('By LC Beneficiary', margin, yPosition);
        if (excludeOutliers && outlierInfo.count > 0) {
          pdf.setFontSize(6);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(180, 120, 0);
          pdf.text(`(${outlierInfo.count} outlier invoice${outlierInfo.count !== 1 ? 's' : ''} excluded)`, margin + 38, yPosition);
        }
        yPosition += 5;

        // Beneficiary table headers
        const benHeaders = ['LC Beneficiary', 'Invoices', 'System FOB', 'Custom Value', 'FOB Var $', 'FOB Var %', 'Duty Var $', 'Tariff Var $', 'Total Var $'];
        const benColWidths = [55, 18, 28, 28, 26, 20, 26, 26, 28];

        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, yPosition, tableWidth, 6, 'F');
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(71, 85, 105);

        xPos = margin + 2;
        benHeaders.forEach((header, i) => {
          pdf.text(header, xPos, yPosition + 4);
          xPos += benColWidths[i];
        });
        yPosition += 8;

        // Beneficiary data rows
        pdf.setFontSize(6);
        pdf.setFont('helvetica', 'normal');
        beneficiaryPivotData.forEach((row, rowIndex) => {
          // Check if we need a new page
          if (yPosition > pageHeight - 20) {
            addFooter();
            pdf.addPage();
            yPosition = addHeader();
          }

          const fobVarPct = row.total_fob > 0 ? (row.fob_variance_amount / row.total_fob) * 100 : 0;
          const totalVariance = row.duty_variance_amount + row.tariff_variance_amount;

          if (rowIndex % 2 === 1) {
            pdf.setFillColor(249, 250, 251);
            pdf.rect(margin, yPosition, tableWidth, 5, 'F');
          }

          xPos = margin + 2;
          pdf.setTextColor(30, 41, 59);
          // Truncate long beneficiary names
          const benName = row.factory.length > 35 ? row.factory.substring(0, 33) + '...' : row.factory;
          pdf.text(benName, xPos, yPosition + 3.5);
          xPos += benColWidths[0];

          pdf.text(row.invoice_count.toString(), xPos, yPosition + 3.5);
          xPos += benColWidths[1];

          pdf.text(formatCurrency(row.total_fob), xPos, yPosition + 3.5);
          xPos += benColWidths[2];

          pdf.text(formatCurrency(row.total_custom_value), xPos, yPosition + 3.5);
          xPos += benColWidths[3];

          pdf.setTextColor(row.fob_variance_amount >= 0 ? 16 : 239, row.fob_variance_amount >= 0 ? 185 : 68, row.fob_variance_amount >= 0 ? 129 : 68);
          pdf.text(formatCurrency(row.fob_variance_amount), xPos, yPosition + 3.5);
          xPos += benColWidths[4];

          pdf.text(fobVarPct.toFixed(1) + '%', xPos, yPosition + 3.5);
          xPos += benColWidths[5];

          pdf.setTextColor(row.duty_variance_amount >= 0 ? 16 : 239, row.duty_variance_amount >= 0 ? 185 : 68, row.duty_variance_amount >= 0 ? 129 : 68);
          pdf.text(formatCurrency(row.duty_variance_amount), xPos, yPosition + 3.5);
          xPos += benColWidths[6];

          pdf.setTextColor(row.tariff_variance_amount >= 0 ? 16 : 239, row.tariff_variance_amount >= 0 ? 185 : 68, row.tariff_variance_amount >= 0 ? 129 : 68);
          pdf.text(formatCurrency(row.tariff_variance_amount), xPos, yPosition + 3.5);
          xPos += benColWidths[7];

          pdf.setTextColor(totalVariance >= 0 ? 16 : 239, totalVariance >= 0 ? 185 : 68, totalVariance >= 0 ? 129 : 68);
          pdf.setFont('helvetica', 'bold');
          pdf.text(formatCurrency(totalVariance), xPos, yPosition + 3.5);
          pdf.setFont('helvetica', 'normal');

          yPosition += 5;
        });

        // Beneficiary grand total row
        const benGrandTotalVar = beneficiaryGrandTotals.duty_variance_amount + beneficiaryGrandTotals.tariff_variance_amount;
        const benGrandFobPct = beneficiaryGrandTotals.total_fob > 0 ? (beneficiaryGrandTotals.fob_variance_amount / beneficiaryGrandTotals.total_fob) * 100 : 0;
        pdf.setFillColor(226, 232, 240);
        pdf.rect(margin, yPosition, tableWidth, 5.5, 'F');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(6);
        pdf.setTextColor(30, 41, 59);

        xPos = margin + 2;
        pdf.text('Grand Total', xPos, yPosition + 4);
        xPos += benColWidths[0];
        pdf.text(beneficiaryGrandTotals.invoice_count.toString(), xPos, yPosition + 4);
        xPos += benColWidths[1];
        pdf.text(formatCurrency(beneficiaryGrandTotals.total_fob), xPos, yPosition + 4);
        xPos += benColWidths[2];
        pdf.text(formatCurrency(beneficiaryGrandTotals.total_custom_value), xPos, yPosition + 4);
        xPos += benColWidths[3];

        pdf.setTextColor(beneficiaryGrandTotals.fob_variance_amount >= 0 ? 16 : 239, beneficiaryGrandTotals.fob_variance_amount >= 0 ? 185 : 68, beneficiaryGrandTotals.fob_variance_amount >= 0 ? 129 : 68);
        pdf.text(formatCurrency(beneficiaryGrandTotals.fob_variance_amount), xPos, yPosition + 4);
        xPos += benColWidths[4];
        pdf.text(benGrandFobPct.toFixed(1) + '%', xPos, yPosition + 4);
        xPos += benColWidths[5];

        pdf.setTextColor(beneficiaryGrandTotals.duty_variance_amount >= 0 ? 16 : 239, beneficiaryGrandTotals.duty_variance_amount >= 0 ? 185 : 68, beneficiaryGrandTotals.duty_variance_amount >= 0 ? 129 : 68);
        pdf.text(formatCurrency(beneficiaryGrandTotals.duty_variance_amount), xPos, yPosition + 4);
        xPos += benColWidths[6];

        pdf.setTextColor(beneficiaryGrandTotals.tariff_variance_amount >= 0 ? 16 : 239, beneficiaryGrandTotals.tariff_variance_amount >= 0 ? 185 : 68, beneficiaryGrandTotals.tariff_variance_amount >= 0 ? 129 : 68);
        pdf.text(formatCurrency(beneficiaryGrandTotals.tariff_variance_amount), xPos, yPosition + 4);
        xPos += benColWidths[7];

        pdf.setTextColor(benGrandTotalVar >= 0 ? 16 : 239, benGrandTotalVar >= 0 ? 185 : 68, benGrandTotalVar >= 0 ? 129 : 68);
        pdf.text(formatCurrency(benGrandTotalVar), xPos, yPosition + 4);

        // Footer on page 2
        addFooter();
      }

      // Save the PDF
      pdf.save(`First_Sale_Review_${startDate}_to_${endDate}.pdf`);

    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '-';
    const absValue = Math.abs(value);
    const formatted = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(absValue);
    return value < 0 ? `(${formatted})` : formatted;
  };

  const formatNumber = (value) => {
    if (value === null || value === undefined) return '-';
    return new Intl.NumberFormat('en-US').format(Math.round(value));
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '-';
    const prefix = value > 0 ? '+' : '';
    return `${prefix}${value.toFixed(2)}%`;
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-1">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color || entry.fill }} className="text-sm">
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const MultiSelectDropdown = ({ label, icon: Icon, options, selected, filterType, filterGroup }) => {
    const dropdownId = `${filterGroup}-${filterType}`;
    const isOpen = openDropdown === dropdownId;

    return (
      <div className="relative">
        <label className="block text-xs font-medium text-gray-700 mb-1">
          {Icon && <Icon size={14} className="inline mr-1" />}
          {label}
        </label>
        <button
          type="button"
          onClick={() => setOpenDropdown(isOpen ? null : dropdownId)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
        >
          <span className={selected.length === 0 ? 'text-gray-400' : 'text-gray-900'}>
            {selected.length === 0 ? `All ${label}` : `${selected.length} selected`}
          </span>
          <ChevronDown size={14} className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
            <div className="sticky top-0 bg-gray-50 border-b px-3 py-2 flex justify-between items-center">
              <span className="text-xs text-gray-600">{selected.length} selected</span>
              <button onClick={() => clearFilter(filterType, filterGroup)} className="text-xs text-blue-600 hover:text-blue-800">Clear</button>
            </div>
            {options.map(option => (
              <label key={option} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                <input
                  type="checkbox"
                  checked={selected.includes(option)}
                  onChange={() => toggleMultiSelect(filterType, filterGroup, option)}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <span className="text-gray-700 truncate">{option}</span>
              </label>
            ))}
            {options.length === 0 && (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">No options</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div ref={containerRef} className={embedded ? "bg-slate-50 p-4" : "p-6 bg-slate-50 min-h-screen"}>
      {/* Header */}
      <div className="mb-6">
        {!embedded && (
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        )}
        <div className="flex items-center justify-between">
          {!embedded && (
            <div>
              <h1 className="text-2xl font-semibold text-gray-900 mb-2">First Sale Review</h1>
              <p className="text-sm text-gray-600">Analysis of FS and FS3 terms by month and commercial invoice</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            {/* PDF Export Button */}
            {fsData.length > 0 && (
              <button
                onClick={exportToPdf}
                disabled={exportingPdf}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg transition-colors bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportingPdf ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Generating PDF...
                  </>
                ) : (
                  <>
                    <FileText size={16} />
                    Export PDF
                  </>
                )}
              </button>
            )}
            <div className="w-px h-6 bg-gray-300 mx-1"></div>
            <button
              onClick={() => setShowPivot(!showPivot)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showPivot ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
            >
              Pivot
            </button>
            <button
              onClick={() => setShowCharts(!showCharts)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showCharts ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}
            >
              <BarChart3 size={16} />
              Charts
            </button>
            <button
              onClick={() => setShowDetail(!showDetail)}
              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${showDetail ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'}`}
            >
              Detail
            </button>
          </div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900">Filters</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">
              <Calendar size={14} className="inline mr-1" />
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
            />
          </div>

          <MultiSelectDropdown
            label="Division"
            icon={Building2}
            options={dropdownOptions.divisions}
            selected={postFilters.division}
            filterType="division"
            filterGroup="post"
          />

          <MultiSelectDropdown
            label="Customer"
            icon={Users}
            options={dropdownOptions.customers}
            selected={postFilters.customer}
            filterType="customer"
            filterGroup="post"
          />

          <MultiSelectDropdown
            label="Office"
            icon={Briefcase}
            options={dropdownOptions.offices}
            selected={postFilters.office}
            filterType="office"
            filterGroup="post"
          />

          <MultiSelectDropdown
            label="Country"
            icon={Globe}
            options={dropdownOptions.countries}
            selected={postFilters.country}
            filterType="country"
            filterGroup="post"
          />
        </div>

        <div className="flex gap-3 mt-4">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Loading...' : 'Load Data'}
          </button>

          {selectedMonth && (
            <button
              onClick={() => setSelectedMonth(null)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-200 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-300"
            >
              Clear Month Filter: {selectedMonth}
            </button>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle size={20} className="text-red-600" />
            <p className="text-sm font-medium text-red-900">{error}</p>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {/* Summary Stats */}
      {!loading && data.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white border border-blue-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <FileText size={16} className="text-blue-600" />
              <span className="text-xs font-medium text-blue-900">FS/FS3 Invoices</span>
            </div>
            <p className="text-2xl font-bold text-blue-900">{formatNumber(stats.totalInvoices)}</p>
          </div>

          <div className="bg-white border border-green-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign size={16} className="text-green-600" />
              <span className="text-xs font-medium text-green-900">Total FOB</span>
            </div>
            <p className="text-2xl font-bold text-green-900">{formatCurrency(stats.totalFob)}</p>
          </div>

          <div className="bg-white border border-orange-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              {stats.fobVarianceAmount >= 0 ? <TrendingUp size={16} className="text-red-600" /> : <TrendingDown size={16} className="text-green-600" />}
              <span className="text-xs font-medium text-gray-900">FOB Variance</span>
            </div>
            <p className={`text-2xl font-bold ${stats.fobVarianceAmount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(stats.fobVarianceAmount)}
            </p>
            <p className={`text-xs ${stats.fobVariancePct >= 0 ? 'text-red-500' : 'text-green-500'}`}>
              {formatPercent(stats.fobVariancePct)}
            </p>
          </div>

          <div className="bg-white border border-purple-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-center gap-2 mb-1">
              {stats.dutyVarianceAmount >= 0 ? <TrendingUp size={16} className="text-red-600" /> : <TrendingDown size={16} className="text-green-600" />}
              <span className="text-xs font-medium text-gray-900">Duty Variance</span>
            </div>
            <p className={`text-2xl font-bold ${stats.dutyVarianceAmount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
              {formatCurrency(stats.dutyVarianceAmount)}
            </p>
            <p className={`text-xs ${stats.dutyVariancePct >= 0 ? 'text-red-500' : 'text-green-500'}`}>
              {formatPercent(stats.dutyVariancePct)}
            </p>
          </div>
        </div>
      )}

      {/* Outlier Exclusion Toggle */}
      {!loading && data.length > 0 && outlierInfo.count > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertCircle size={18} className="text-amber-600" />
            <div>
              <span className="text-sm font-medium text-amber-900">
                {outlierInfo.count} invoice{outlierInfo.count !== 1 ? 's' : ''} with outlier FOB Var %
              </span>
              <span className="text-xs text-amber-700 ml-2">
                (outside {outlierInfo.lowerBound?.toFixed(1)}% to {outlierInfo.upperBound?.toFixed(1)}% range)
              </span>
            </div>
          </div>
          <button
            onClick={() => setExcludeOutliers(!excludeOutliers)}
            className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              excludeOutliers
                ? 'bg-amber-600 text-white hover:bg-amber-700'
                : 'bg-white text-amber-700 border border-amber-300 hover:bg-amber-100'
            }`}
          >
            {excludeOutliers ? 'Show All Invoices' : 'Exclude Outliers'}
          </button>
        </div>
      )}

      {/* Pivot Table */}
      {!loading && data.length > 0 && showPivot && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden mb-6 shadow-sm">
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">📋 Monthly Summary - FS/FS3 Terms</h3>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
            >
              <Download size={14} />
              Export Excel
            </button>
          </div>

          {/* Pivot Filter Dropdowns */}
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-3 gap-3">
              <div className="relative">
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-customer' ? null : 'pivot-customer')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.customer.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.customer.length === 0 ? 'All Customers' : `${pivotFilters.customer.length} Customers`}
                  </span>
                  <ChevronDown size={12} />
                </button>
                {openDropdown === 'pivot-customer' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.customer.length} selected</span>
                      <button onClick={() => clearFilter('customer', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.customers.map(c => (
                      <label key={c} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.customer.includes(c)} onChange={() => toggleMultiSelect('customer', 'pivot', c)} className="w-3 h-3" />
                        <span className="truncate">{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-office' ? null : 'pivot-office')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.office.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.office.length === 0 ? 'All Offices' : `${pivotFilters.office.length} Offices`}
                  </span>
                  <ChevronDown size={12} />
                </button>
                {openDropdown === 'pivot-office' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.office.length} selected</span>
                      <button onClick={() => clearFilter('office', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.offices.map(o => (
                      <label key={o} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.office.includes(o)} onChange={() => toggleMultiSelect('office', 'pivot', o)} className="w-3 h-3" />
                        <span className="truncate">{o}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="relative">
                <button
                  onClick={() => setOpenDropdown(openDropdown === 'pivot-country' ? null : 'pivot-country')}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg bg-white text-left flex items-center justify-between"
                >
                  <span className={pivotFilters.country.length === 0 ? 'text-gray-400' : 'text-gray-700'}>
                    {pivotFilters.country.length === 0 ? 'All Countries' : `${pivotFilters.country.length} Countries`}
                  </span>
                  <ChevronDown size={12} />
                </button>
                {openDropdown === 'pivot-country' && (
                  <div className="absolute z-50 mt-1 w-48 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    <div className="sticky top-0 bg-gray-50 border-b px-2 py-1.5 flex justify-between">
                      <span className="text-xs text-gray-500">{pivotFilters.country.length} selected</span>
                      <button onClick={() => clearFilter('country', 'pivot')} className="text-xs text-blue-600">Clear</button>
                    </div>
                    {dropdownOptions.countries.map(c => (
                      <label key={c} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-xs">
                        <input type="checkbox" checked={pivotFilters.country.includes(c)} onChange={() => toggleMultiSelect('country', 'pivot', c)} className="w-3 h-3" />
                        <span className="truncate">{c}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">Month</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Invoices</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">System FOB</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Custom Value</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">FOB Variance $</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">FOB Var %</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Duty Variance $</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Tariff Variance $</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Total Variance $</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {pivotData.map(row => {
                  const fobVariancePct = row.total_fob > 0 ? (row.fob_variance_amount / row.total_fob) * 100 : 0;
                  const totalVariance = row.duty_variance_amount + row.tariff_variance_amount;
                  return (
                    <tr
                      key={row.month}
                      className={`hover:bg-blue-50 cursor-pointer transition-colors ${selectedMonth === row.month ? 'bg-blue-100' : ''}`}
                      onClick={() => setSelectedMonth(selectedMonth === row.month ? null : row.month)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                        {selectedMonth === row.month ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {row.month}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{row.invoice_count}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_fob)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_custom_value)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${row.fob_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(row.fob_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${fobVariancePct >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatPercent(fobVariancePct)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.duty_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(row.duty_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.tariff_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(row.tariff_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${totalVariance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(totalVariance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-200 border-t-2 border-gray-300">
                {(() => {
                  const grandTotalVariance = grandTotals.duty_variance_amount + grandTotals.tariff_variance_amount;
                  return (
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-900">Grand Total</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{grandTotals.invoice_count}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_fob)}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(grandTotals.total_custom_value)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${grandTotals.fob_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(grandTotals.fob_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${stats.fobVariancePct >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatPercent(stats.fobVariancePct)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${grandTotals.duty_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(grandTotals.duty_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${grandTotals.tariff_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(grandTotals.tariff_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${grandTotalVariance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(grandTotalVariance)}
                      </td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* By LC Beneficiary Table */}
      {!loading && data.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm mb-6">
          <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white">
            <div className="flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Building2 size={18} className="text-emerald-600" />
                By LC Beneficiary
                {selectedMonth && <span className="text-sm font-normal text-blue-600 ml-2">- {selectedMonth}</span>}
                {selectedBeneficiary && <span className="text-sm font-normal text-emerald-600 ml-2">- {(lcLookup[selectedBeneficiary] || selectedBeneficiary).substring(0, 30)}</span>}
              </h2>
              <div className="flex items-center gap-2">
                {selectedBeneficiary && (
                  <button
                    onClick={() => setSelectedBeneficiary(null)}
                    className="flex items-center gap-1 px-2 py-1 bg-emerald-200 text-emerald-700 text-xs font-medium rounded hover:bg-emerald-300"
                  >
                    Clear
                  </button>
                )}
                <span className="text-sm text-gray-500">{beneficiaryPivotData.length} beneficiaries</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-100 border-b border-gray-200 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-gray-700">LC Beneficiary</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Invoices</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">System FOB</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Custom Value</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">FOB Variance $</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">FOB Var %</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Duty Variance $</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Tariff Variance $</th>
                  <th className="px-4 py-3 text-right font-semibold text-gray-700">Total Variance $</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {beneficiaryPivotData.map(row => {
                  const fobVariancePct = row.total_fob > 0 ? (row.fob_variance_amount / row.total_fob) * 100 : 0;
                  const totalVariance = row.duty_variance_amount + row.tariff_variance_amount;
                  return (
                    <tr
                      // CHA-404 — key on the CODE, not the resolved company name. The rows are
                      // grouped by code, but the location master resolves 17 name collisions
                      // (JIAGUU and JIAGGG are both "Jiangsu Guotai…"), so keying on the name
                      // would collide the moment real names started resolving.
                      key={row.lc_code}
                      className={`hover:bg-emerald-50 cursor-pointer transition-colors ${selectedBeneficiary === row.lc_code ? 'bg-emerald-100 ring-1 ring-emerald-400' : ''}`}
                      onClick={() => setSelectedBeneficiary(prev => prev === row.lc_code ? null : row.lc_code)}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 flex items-center gap-2">
                        {selectedBeneficiary === row.lc_code ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        <span className="truncate max-w-[250px]" title={row.factory}>{row.factory}</span>
                      </td>
                      <td className="px-4 py-3 text-right text-gray-700">{row.invoice_count}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_fob)}</td>
                      <td className="px-4 py-3 text-right text-gray-700">{formatCurrency(row.total_custom_value)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${row.fob_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(row.fob_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${fobVariancePct >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatPercent(fobVariancePct)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.duty_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(row.duty_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-medium ${row.tariff_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(row.tariff_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${totalVariance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(totalVariance)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-200 border-t-2 border-gray-300 sticky bottom-0">
                {(() => {
                  const grandTotalVariance = beneficiaryGrandTotals.duty_variance_amount + beneficiaryGrandTotals.tariff_variance_amount;
                  const fobVarPct = beneficiaryGrandTotals.total_fob > 0 ? (beneficiaryGrandTotals.fob_variance_amount / beneficiaryGrandTotals.total_fob) * 100 : 0;
                  return (
                    <tr>
                      <td className="px-4 py-3 font-bold text-gray-900">Grand Total</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{beneficiaryGrandTotals.invoice_count}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(beneficiaryGrandTotals.total_fob)}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatCurrency(beneficiaryGrandTotals.total_custom_value)}</td>
                      <td className={`px-4 py-3 text-right font-bold ${beneficiaryGrandTotals.fob_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(beneficiaryGrandTotals.fob_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${fobVarPct >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatPercent(fobVarPct)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${beneficiaryGrandTotals.duty_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(beneficiaryGrandTotals.duty_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${beneficiaryGrandTotals.tariff_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(beneficiaryGrandTotals.tariff_variance_amount)}
                      </td>
                      <td className={`px-4 py-3 text-right font-bold ${grandTotalVariance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(grandTotalVariance)}
                      </td>
                    </tr>
                  );
                })()}
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Charts Section */}
      {!loading && data.length > 0 && showCharts && (
        <div ref={chartsRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {/* Country Distribution */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Globe size={16} className="text-blue-600" />
              FOB by Country
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={countryData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                >
                  {countryData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* FOB by Office */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Briefcase size={16} className="text-purple-600" />
              FOB by Office
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={officeData}
                  cx="50%"
                  cy="50%"
                  innerRadius={45}
                  outerRadius={85}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                  labelLine={{ stroke: '#6b7280', strokeWidth: 1 }}
                >
                  {officeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Customer Impact */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users size={16} className="text-purple-600" />
              FOB Variance by Customer
            </h3>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={customerImpactData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 9 }} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="value" name="FOB Variance" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Hierarchical Detail Table */}
      {!loading && data.length > 0 && showDetail && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          <div className="bg-gray-50 border-b border-gray-200 p-4 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">
              📄 Invoice Details ({monthFilteredInvoices.length} invoices)
              {selectedMonth && <span className="ml-2 text-blue-600">- {selectedMonth}</span>}
              {selectedBeneficiary && <span className="ml-2 text-emerald-600">- {(lcLookup[selectedBeneficiary] || selectedBeneficiary).substring(0, 40)}</span>}
            </h3>
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
            >
              <Download size={14} />
              Export Excel
            </button>
          </div>

          <div className="overflow-x-auto max-h-[600px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-100 border-b border-gray-200 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 w-8"></th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Invoice / PO / Style</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Month</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Terms</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Customer</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">PO Manager</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700">Shipment</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">PO Count</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">System FOB</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Custom Value</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">FOB Var %</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700">Duty Var $</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {hierarchicalData.slice(0, 100).map((inv) => (
                  <React.Fragment key={inv.commercial_invoice}>
                    {/* Invoice Row */}
                    <tr
                      className="bg-blue-50 hover:bg-blue-100 cursor-pointer font-medium"
                      onClick={() => toggleInvoice(inv.commercial_invoice)}
                    >
                      <td className="px-3 py-2">
                        {expandedInvoices.has(inv.commercial_invoice) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </td>
                      <td className="px-3 py-2 text-blue-900">{inv.commercial_invoice}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.receipt_month}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.terms}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.customer}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.production_manager || '-'}</td>
                      <td className="px-3 py-2 text-blue-800">{inv.shipmentNumbers?.join(', ') || '-'}</td>
                      <td className="px-3 py-2 text-right text-blue-800">{inv.po_count}</td>
                      <td className="px-3 py-2 text-right text-blue-900">{formatCurrency(inv.total_fob)}</td>
                      <td className="px-3 py-2 text-right text-blue-900">{formatCurrency(inv.total_line_entered_value)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${inv.fobVariance >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatPercent(inv.fobVariance)}
                      </td>
                      <td className={`px-3 py-2 text-right font-bold ${inv.duty_variance_amount >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {formatCurrency(inv.duty_variance_amount)}
                      </td>
                    </tr>

                    {/* PO Rows */}
                    {expandedInvoices.has(inv.commercial_invoice) && inv.pos.map((po) => (
                      <React.Fragment key={`${inv.commercial_invoice}-${po.po}`}>
                        <tr
                          className="bg-gray-50 hover:bg-gray-100 cursor-pointer"
                          onClick={() => togglePO(`${inv.commercial_invoice}-${po.po}`)}
                        >
                          <td className="px-3 py-2 pl-6">
                            {expandedPOs.has(`${inv.commercial_invoice}-${po.po}`) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                          </td>
                          <td className="px-3 py-2 text-gray-700 pl-4">↳ PO: {po.po}</td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2"></td>
                          <td className="px-3 py-2 text-gray-600">{po.shipmentNumbers?.join(', ') || '-'}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{po.styles.length}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(po.totals.fob)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{formatCurrency(po.totals.custom_value)}</td>
                          <td className={`px-3 py-2 text-right ${po.totals.fob > 0 && ((po.totals.custom_value - po.totals.fob) / po.totals.fob) >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {po.totals.fob > 0 ? formatPercent(((po.totals.custom_value - po.totals.fob) / po.totals.fob) * 100) : '-'}
                          </td>
                          <td className="px-3 py-2"></td>
                        </tr>

                        {/* Style Rows */}
                        {expandedPOs.has(`${inv.commercial_invoice}-${po.po}`) && po.styles.map((style, idx) => (
                          <tr key={`${inv.commercial_invoice}-${po.po}-${idx}`} className="hover:bg-gray-50">
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 text-gray-500 pl-10">↳ {style.style || '-'} / {style.color || '-'}</td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5"></td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatNumber(style.qty)}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatCurrency(style.fob)}</td>
                            <td className="px-3 py-1.5 text-right text-gray-500">{formatCurrency(style.custom_value)}</td>
                            <td className={`px-3 py-1.5 text-right ${style.variance >= 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {formatPercent(style.variance)}
                            </td>
                            <td className="px-3 py-1.5"></td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {hierarchicalData.length > 100 && (
            <div className="bg-yellow-50 border-t border-yellow-200 px-4 py-3">
              <p className="text-xs text-yellow-800">Showing first 100 of {hierarchicalData.length} invoices. Export to Excel to see all.</p>
            </div>
          )}
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && fsData.length === 0 && (
        <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
          <Package size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-600 font-medium">No FS/FS3 records found</p>
          <p className="text-sm text-gray-500 mt-1">Click "Load Data" to fetch data for the selected date range</p>
        </div>
      )}
    </div>
  );
}
